import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type RevealProps = {
  children: ReactNode;
  className?: string;
  delayMs?: number;
  rootMargin?: string;
  threshold?: number;
};

export function Reveal({
  children,
  className,
  delayMs = 0,
  rootMargin = '0px 0px 48px 0px',
  threshold = 0.05,
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    const revealIfVisible = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < vh && r.bottom > 0) setShown(true);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShown(true);
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    requestAnimationFrame(revealIfVisible);

    return () => io.disconnect();
  }, [reduceMotion, rootMargin, threshold]);

  return (
    <div
      ref={ref}
      className={cn(
        'transition-[opacity,transform] duration-900 ease-kuiva',
        shown ? 'translate-y-0 opacity-100' : 'translate-y-9 opacity-0',
        className,
      )}
      style={{ transitionDelay: reduceMotion ? '0ms' : `${delayMs}ms` }}
    >
      {children}
    </div>
  );
}
